/**
 * driveSync — thin client-side wrapper around the portal-to-Drive mirror (A.10).
 *
 * The portal calls `kick(documentId)` when staff APPROVE a document (see
 * src/components/DocumentViewer.tsx handleStatusChange). Approval is the gate:
 * only approved documents are mirrored to Drive (Batch 4C item 2). This fires
 * the sync-doc-to-drive edge function fire-and-forget. Uploads themselves no
 * longer kick the sync.
 *
 * CARDINAL RULE (plan section 6): a Drive sync failure must NEVER fail or stall
 * the portal upload. `kick` returns synchronously (void) and swallows every
 * error. On any failure it falls back to enqueuing the document via the
 * enqueue_drive_sync RPC so the pg_cron worker retries. Nothing is thrown to
 * the caller; everything is logged to console + Sentry.
 */

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';

/**
 * Feature flag (A.10). Drive sync is PARKED: the sync-doc-to-drive edge
 * function is not deployed (blocked on GCP service-account auth + Workspace
 * domain-wide delegation). Until it ships, `kick` must no-op — otherwise every
 * upload fires a failing function invoke, reports a Sentry error, and enqueues
 * a drive_sync_queue row that nothing drains.
 *
 * To re-enable when Drive sync goes live: set VITE_DRIVE_SYNC_ENABLED=true in
 * the Vercel project env and redeploy. Default (unset) keeps it off.
 */
const DRIVE_SYNC_ENABLED = import.meta.env.VITE_DRIVE_SYNC_ENABLED === 'true';

/**
 * Fire the Drive sync for a newly-approved document. Fire-and-forget: this
 * function returns immediately and does NOT block the caller's return path.
 *
 * Flow:
 *   1. Invoke sync-doc-to-drive (hot path). On 2xx, done.
 *   2. On any throw (network blip, function unreachable) OR a 503 response
 *      (Drive down / not configured / rate-limited), enqueue a retry row so
 *      the cron worker picks it up.
 *   3. All errors are logged; none propagate to the caller.
 */
export function kick(documentId: string): void {
  if (!DRIVE_SYNC_ENABLED) return;
  if (!documentId) return;

  // Detach from the caller's await chain entirely. We intentionally do not
  // return the promise so the upload's return contract is unaffected.
  void (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('sync-doc-to-drive', {
        body: { document_id: documentId },
      });

      if (error) {
        // supabase-js surfaces non-2xx responses (including the function's 503)
        // as an error here. Fall back to the retry queue.
        await enqueueFallback(documentId, error);
        return;
      }

      // The edge function returns 200 with { skipped: ... } for benign cases
      // (document deleted, no bytes). Those are not failures; nothing to do.
      void data;
    } catch (err) {
      // Network error / function totally unreachable. Enqueue for retry.
      await enqueueFallback(documentId, err);
    }
  })();
}

/**
 * Fallback path: record the document in drive_sync_queue so the cron worker
 * retries. Best-effort — if even the enqueue fails we log and give up (the
 * staff "Sync this matter to Drive" button is the manual recovery).
 */
async function enqueueFallback(documentId: string, cause: unknown): Promise<void> {
  reportSoft('drive-sync invoke failed; enqueuing retry', cause, documentId);
  try {
    // enqueue_drive_sync isn't in the generated database.types.ts yet (added by
    // migration 20260521000002). Cast around it, same pattern as other
    // recently-added DB objects in this codebase (see uploadDocument.ts).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.rpc as any)('enqueue_drive_sync', { document_id: documentId });
    if (error) reportSoft('drive-sync enqueue RPC failed', error, documentId);
  } catch (err) {
    reportSoft('drive-sync enqueue RPC threw', err, documentId);
  }
}

/** Log to console + Sentry without ever throwing. */
function reportSoft(message: string, cause: unknown, documentId: string): void {
  // eslint-disable-next-line no-console
  console.warn(`[driveSync] ${message}`, { documentId, cause });
  try {
    Sentry.captureException(cause instanceof Error ? cause : new Error(message), {
      tags: { feature: 'drive-sync' },
      extra: { documentId, message },
    });
  } catch {
    /* Sentry not initialised — console log above is enough. */
  }
}

export const driveSync = { kick };
